import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useReducedMotion,
  useSpring,
} from "framer-motion";
import {
  Sparkles,
  TrendingUp,
  Heart,
  Smartphone,
  Globe,
  Bot,
  Database,
  ArrowRight,
  Check,
  SlidersHorizontal,
  Share2,
  ShieldCheck,
  Instagram,
} from "lucide-react";

// Brand tokens (cyan / dark-navy) — see src/styles/brand-v2.css
import "../styles/brand-v2.css";

// Runtime asset helpers for /public/landing-v2/* (final ZIP assets only).
import { lpImg, lpVideo } from "../utils/landingAssets";

// LazyVideo: muted/looped, plays only in viewport, poster fallback under
// prefers-reduced-motion.
import LazyVideo from "../components/landing-v2/LazyVideo";

// Global language toggle (DE/EN/HR) — the single react-i18next switcher.
import LanguageSwitcher from "../components/LanguageSwitcher";
import { usePricing } from "../hooks/usePricing";

/**
 * Landing V2 — Launch redesign (2026-06-14), fully i18n-wired (2026-07).
 *
 * The official `/` landing. Every string routes through the shared
 * react-i18next `landing` namespace (DE/EN/HR) — same system as the rest
 * of the app. The header LanguageSwitcher flips all copy instantly and
 * the choice survives reloads (localStorage-backed language detector).
 */

// --- Theme tokens ----------------------------------------------------- //
const T = {
  bg: "bg-[var(--brand-navy-900)]",
  text: "text-[#E6E8EC]",
  muted: "text-[#9AA0AA]",
  dim: "text-[#6B7280]",
};

const EASE = [0.22, 1, 0.36, 1];

// CTA destinations.
const REGISTER = "/register";
const LOGIN = "/login";
// Official DealAlert iOS App Store listing.
const APP_STORE_URL = "https://apps.apple.com/app/id6778237706";
// TODO: Replace with real Google Play URL once the Android app is published.
const GOOGLE_PLAY_URL = "/register";

// --- Small primitives ------------------------------------------------- //
function Reveal({ children, className = "", delay = 0, y = 26 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12%" }}
      transition={{ duration: 0.6, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

function Eyebrow({ children, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-2 rounded-full border border-[rgba(34,211,238,0.30)] bg-[rgba(34,211,238,0.06)] px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--brand-cyan)]"
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--brand-cyan)]" />
      {children}
    </span>
  );
}

// --- Device frames ---------------------------------------------------- //
// Both frames FILL their parent. The parent controls size + aspect.
function PhoneFrame({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative h-full w-full rounded-[2.3rem] bg-[var(--brand-navy-deep)] p-[7px] ring-1 ring-white/[0.14] shadow-[0_40px_90px_-26px_rgba(0,0,0,0.78),inset_0_0_0_1px_rgba(255,255,255,0.05)] ${className}`}
    >
      <div className="relative h-full w-full overflow-hidden rounded-[1.85rem] bg-[var(--brand-navy-800)]">
        <div className="absolute top-0 left-1/2 z-20 h-[16px] w-[34%] -translate-x-1/2 rounded-b-[11px] bg-black/95" />
        {children}
      </div>
    </div>
  );
}

function BrowserFrame({ children, url = "dealalert.ai", className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative w-full overflow-hidden rounded-xl bg-[var(--brand-navy-card)] ring-1 ring-white/[0.12] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.82)] ${className}`}
    >
      <div className="flex h-9 items-center gap-2 border-b border-white/[0.07] bg-white/[0.02] px-3.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/80" />
        <div className="ml-3 flex h-5 max-w-[280px] flex-1 items-center rounded-md border border-white/[0.06] bg-white/[0.05] px-2.5">
          <span className="truncate text-[10px] text-[var(--brand-grey-mute)]">{url}</span>
        </div>
      </div>
      <div className="relative aspect-[1800/1068] w-full overflow-hidden">{children}</div>
    </div>
  );
}

const screenImg = "absolute inset-0 h-full w-full object-cover object-top";

// --- Store badges (App Store / Google Play) --------------------------- //
function AppleGlyph() {
  return (
    <svg viewBox="0 0 384 512" className="h-6 w-6 fill-current" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C73.3 141.2 24 184.8 24 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function GooglePlayGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
        fill="var(--brand-cyan)"
        d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54z"
      />
    </svg>
  );
}

function StoreBadges({ className = "" }) {
  const { t } = useTranslation("landing");
  const base =
    "group inline-flex items-center gap-3 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 transition-colors hover:border-white/30 hover:bg-white/[0.08]";
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`} data-testid="store-badges">
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noreferrer"
        data-testid="badge-app-store"
        className={base}
        aria-label={t("store.app_store_aria")}
      >
        <span className="text-white">
          <AppleGlyph />
        </span>
        <span className="flex flex-col leading-tight">
          <span className="text-[10px] uppercase tracking-wide text-[var(--brand-white-soft)]">{t("store.app_store_top")}</span>
          <span className="text-sm font-semibold text-white">{t("store.app_store_name")}</span>
        </span>
      </a>
      {/* TODO: Replace with real Google Play URL */}
      <Link to={GOOGLE_PLAY_URL} data-testid="badge-google-play" className={base} aria-label={t("store.google_play_aria")}>
        <GooglePlayGlyph />
        <span className="flex flex-col leading-tight">
          <span className="text-[10px] uppercase tracking-wide text-[var(--brand-white-soft)]">{t("store.google_play_top")}</span>
          <span className="text-sm font-semibold text-white">{t("store.google_play_name")}</span>
        </span>
      </Link>
    </div>
  );
}

// --- Top nav ---------------------------------------------------------- //
const NAV_LINKS = [
  { href: "#film", key: "product" },
  { href: "#ai-advisor", key: "ai_advisor" },
  { href: "#pricing", key: "pricing" },
];

function TopNav() {
  const { t } = useTranslation("landing");
  return (
    <nav
      data-testid="landing-v2-nav"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/[0.06] bg-[var(--brand-navy-900)]/75 backdrop-blur-md"
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-5 lg:px-10">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-v2-brand">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[var(--brand-cyan)] shadow-[0_0_16px_rgba(34,211,238,0.7)]" />
          <span className="text-[15px] font-semibold tracking-tight text-white">DealAlert</span>
        </Link>
        <div className="hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="text-sm text-[var(--brand-white-soft)] transition-colors hover:text-white">
              {t(`nav.${l.key}`)}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher className="inline-flex" />
          <Link
            to={LOGIN}
            data-testid="landing-v2-login"
            className="hidden rounded-md px-3 py-1.5 text-sm text-[var(--brand-white-soft)] transition-colors hover:text-white sm:inline-flex"
          >
            {t("nav.login")}
          </Link>
          <Link
            to={REGISTER}
            data-testid="landing-v2-nav-cta"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--brand-cyan)] px-4 py-1.5 text-sm font-semibold text-[var(--brand-navy-card)] transition-all hover:brightness-110"
          >
            {t("nav.cta")}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </nav>
  );
}

// --- Hero ------------------------------------------------------------- //
function HeroSection() {
  const { t } = useTranslation("landing");
  const badges = t("hero.badges", { returnObjects: true }) || [];
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const mx = useSpring(0, { stiffness: 60, damping: 18, mass: 0.7 });
  const my = useSpring(0, { stiffness: 60, damping: 18, mass: 0.7 });
  const browserX = useTransform(mx, (v) => v * 0.35);
  const browserY = useTransform(my, (v) => v * 0.35);
  const phoneX = useTransform(mx, (v) => v * -0.8);
  const phoneY = useTransform(my, (v) => v * -0.8);

  useEffect(() => {
    if (reduce) return undefined;
    const onMove = (e) => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const nx = (e.clientX - (rect.left + rect.width / 2)) / rect.width;
      const ny = (e.clientY - (rect.top + rect.height / 2)) / rect.height;
      mx.set(nx * 22);
      my.set(ny * 18);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my, reduce]);

  return (
    <section
      ref={ref}
      data-testid="landing-v2-hero"
      className={`relative ${T.bg} overflow-hidden pt-24 lg:pt-28`}
    >
      {/* glow + grid backdrop */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_55%_0%,rgba(34,211,238,0.12),transparent_72%)]" />
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:64px_64px]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 pb-12 lg:grid-cols-[1fr_1.05fr] lg:px-10 lg:pb-16">
        {/* copy */}
        <div className="max-w-xl">
          <Eyebrow testId="landing-v2-hero-eyebrow">{t("hero.eyebrow")}</Eyebrow>
          <h1 className="mt-5 text-[2.6rem] font-bold leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]">
            {t("hero.title_main")}{" "}
            <span className="bg-gradient-to-r from-[var(--brand-cyan)] to-[#7DE7F5] bg-clip-text text-transparent">{t("hero.title_accent")}</span>
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-[var(--brand-white-soft)]">
            {t("hero.subtitle")}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={REGISTER}
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-cyan)] px-6 py-3 text-base font-semibold text-[var(--brand-navy-card)] shadow-[0_10px_40px_-12px_rgba(34,211,238,0.7)] transition-all hover:brightness-110"
            >
              {t("hero.cta")}
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
          </div>
          <StoreBadges className="mt-5" />
          <ul data-testid="landing-v2-hero-badges" className="mt-8 flex flex-wrap items-center gap-2">
            {badges.map((b) => (
              <li
                key={b}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-[var(--brand-white-soft)]"
              >
                <span className="inline-block h-1 w-1 rounded-full bg-[var(--brand-cyan)]" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* device cluster — DESKTOP: overlap */}
        <div className="relative hidden lg:block">
          <div className="relative mx-auto h-[520px] w-full max-w-[640px]">
            <motion.div style={{ x: browserX, y: browserY }} className="absolute left-0 top-[8%] z-10 w-[84%]">
              <BrowserFrame testId="hero-browser" url="dealalert.ai/dashboard">
                <LazyVideo
                  sources={{ mp4: lpVideo.desktop_dashboard.mp4 }}
                  poster={lpVideo.desktop_dashboard.poster}
                  className={screenImg}
                  ariaLabel="DealAlert Web Dashboard"
                  data-testid="hero-desktop-video"
                />
              </BrowserFrame>
            </motion.div>
            <motion.div
              style={{ x: phoneX, y: phoneY }}
              className="absolute bottom-0 right-0 z-20 aspect-[300/620] h-[86%]"
            >
              <PhoneFrame testId="hero-phone">
                <LazyVideo
                  sources={{ mp4: lpVideo.mobile_dealflow.mp4 }}
                  poster={lpVideo.mobile_dealflow.poster}
                  className={screenImg}
                  ariaLabel="DealAlert DealFlow Swipe"
                  data-testid="hero-mobile-video"
                />
              </PhoneFrame>
            </motion.div>
            <div className="pointer-events-none absolute -inset-10 -z-10 bg-[radial-gradient(45%_45%_at_60%_55%,rgba(34,211,238,0.16),transparent_70%)]" />
          </div>
        </div>

        {/* device cluster — MOBILE: stacked */}
        <div className="lg:hidden">
          <div className="mx-auto mb-6 aspect-[300/620] w-[200px]">
            <PhoneFrame>
              <LazyVideo
                sources={{ mp4: lpVideo.mobile_dealflow.mp4 }}
                poster={lpVideo.mobile_dealflow.poster}
                className={screenImg}
                ariaLabel="DealAlert DealFlow Swipe"
              />
            </PhoneFrame>
          </div>
          <BrowserFrame url="dealalert.ai/dashboard">
            <LazyVideo
              sources={{ mp4: lpVideo.desktop_dashboard.mp4 }}
              poster={lpVideo.desktop_dashboard.poster}
              className={screenImg}
              ariaLabel="DealAlert Web Dashboard"
            />
          </BrowserFrame>
        </div>
      </div>
    </section>
  );
}

// --- Proof strip ------------------------------------------------------ //
const PROOF_ICONS = [Globe, Smartphone, Bot, TrendingUp, Database];

// Sprint R2 Block 7 — public landing stats. Tiny hook so the
// ProofStrip can render real numbers from `/api/public/stats` instead
// of static "Live-Datenpipeline" copy. No auth, 5-min server cache;
// failures stay silent (we fall back to the static badges).
function usePublicStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const ctrl = new AbortController();
    const base =
      process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== "undefined" ? window.location.origin : "");
    fetch(`${base}/api/public/stats`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setStats(data);
      })
      .catch(() => {
        /* network/parse error — leave stats null and fall back */
      });
    return () => ctrl.abort();
  }, []);
  return stats;
}

function ProofStrip() {
  const { t, i18n } = useTranslation("landing");
  const stats = usePublicStats();
  const items = t("proof.items", { returnObjects: true }) || [];
  const localeTag = { de: "de-AT", en: "en-GB", hr: "hr-HR" }[
    (i18n.resolvedLanguage || "de").split("-")[0]
  ] || "de-AT";
  const formatN = (n) => Number(n || 0).toLocaleString(localeTag);
  return (
    <section data-testid="landing-v2-proof" className={`${T.bg} border-y border-white/[0.06] py-7`}>
      <div className="mx-auto max-w-7xl px-5 lg:px-10 space-y-4">
        {stats && (
          <div
            data-testid="landing-v2-public-stats"
            className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-center"
          >
            <div className="flex flex-col gap-0.5">
              <span
                className="text-2xl font-semibold text-[var(--brand-white-soft)] tabular-nums"
                data-testid="public-stats-listings-total"
              >
                {formatN(stats.listings_active_total)}
              </span>
              <span className="text-[11px] uppercase tracking-wider text-[var(--brand-grey-mute)]">
                {t("proof.stats.active_listings")}
              </span>
            </div>
            <div className="hidden sm:block h-8 w-px bg-white/[0.08]" aria-hidden="true" />
            <div className="flex flex-col gap-0.5">
              <span
                className="text-2xl font-semibold text-[var(--brand-cyan)] tabular-nums"
                data-testid="public-stats-auto"
              >
                {formatN(stats.auto_active)}
              </span>
              <span className="text-[11px] uppercase tracking-wider text-[var(--brand-grey-mute)]">
                {t("proof.stats.auto")}
              </span>
            </div>
            <div className="hidden sm:block h-8 w-px bg-white/[0.08]" aria-hidden="true" />
            <div className="flex flex-col gap-0.5">
              <span
                className="text-2xl font-semibold text-[var(--brand-cyan)] tabular-nums"
                data-testid="public-stats-immo"
              >
                {formatN(stats.immo_active)}
              </span>
              <span className="text-[11px] uppercase tracking-wider text-[var(--brand-grey-mute)]">
                {t("proof.stats.immo")}
              </span>
            </div>
            <div className="hidden sm:block h-8 w-px bg-white/[0.08]" aria-hidden="true" />
            <div className="flex flex-col gap-0.5">
              <span
                className="text-2xl font-semibold text-[var(--brand-white-soft)] tabular-nums"
                data-testid="public-stats-countries"
              >
                {formatN(stats.countries_count)}
              </span>
              <span className="text-[11px] uppercase tracking-wider text-[var(--brand-grey-mute)]">
                {stats.countries_count === 1 ? t("proof.stats.country_one") : t("proof.stats.country_other")}
              </span>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
          {items.map((label, i) => {
            const Icon = PROOF_ICONS[i] || Globe;
            return (
              <span
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-2 text-[12px] font-medium text-[var(--brand-white-soft)]"
              >
                <Icon className="h-3.5 w-3.5 text-[var(--brand-cyan)]" strokeWidth={2} />
                {label}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// --- Product Film (sticky) ------------------------------------------- //
// Each beat is visually distinct: front phone + a beat-specific "backdrop"
// (landscape browser for 01-03, a second phone for 04 "Vergleich", a portrait
// AI desktop panel for 05 "AI Advisor"). No two beats share the same desktop
// visual. Text comes from the i18n `landing.film.beats.<key>` tree.
const BEATS = [
  {
    num: "01",
    key: "dealflow",
    video: true,
    back: { type: "browser", src: lpImg.dealflow.desktop, url: "dealalert.ai/deals" },
  },
  {
    num: "02",
    key: "topdeals",
    phone: lpImg.topDeals.mobile,
    back: { type: "browser", src: lpImg.topDeals.desktop, url: "dealalert.ai/top-deals" },
  },
  {
    num: "03",
    key: "dealcard",
    phone: lpImg.dealcardDetail.mobile,
    back: { type: "browser", src: lpImg.dealcardDetail.desktop, url: "dealalert.ai/listing" },
  },
  {
    num: "04",
    key: "detail",
    phone: lpImg.compare.mobile,
    // Second phone (the description/detail view) → clearly "Vergleich".
    back: { type: "phone", src: lpImg.dealcardDetail.mobile_description },
  },
  {
    num: "05",
    key: "advisor",
    phone: lpImg.aiAdvisor.mobile,
    emphasizePhone: true,
    // Portrait AI desktop chat panel → clearly "AI Advisor".
    back: { type: "panel", src: lpImg.aiAdvisor.desktop, url: "dealalert.ai/advisor" },
  },
];

function FilmStepper({ active }) {
  return (
    <div data-testid="film-stepper" className="mb-8 flex items-center gap-2.5">
      {BEATS.map((b, i) => {
        const isActive = i === active;
        const done = i < active;
        return (
          <div key={b.num} className="flex items-center gap-2.5">
            <span
              className={`rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums transition-all duration-300 ${
                isActive
                  ? "bg-[rgba(34,211,238,0.14)] text-[var(--brand-cyan)]"
                  : done
                    ? "text-[var(--brand-white-soft)]"
                    : "text-[var(--brand-grey-mute)]"
              }`}
            >
              {b.num}
            </span>
            {i < BEATS.length - 1 && (
              <span className="relative block h-px w-8 overflow-hidden rounded bg-white/10">
                <motion.span
                  className="absolute inset-0 origin-left bg-[var(--brand-cyan)]"
                  initial={false}
                  animate={{ scaleX: i < active ? 1 : 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Portrait app window (mac-style) for the AI desktop chat (tall asset).
function PortraitPanel({ children, url }) {
  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-xl bg-[var(--brand-navy-card)] ring-1 ring-white/[0.12] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.82)]">
      <div className="flex h-8 shrink-0 items-center gap-2 border-b border-white/[0.07] bg-white/[0.02] px-3">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/80" />
        <div className="ml-3 flex h-5 max-w-[180px] flex-1 items-center rounded-md border border-white/[0.06] bg-white/[0.05] px-2.5">
          <span className="truncate text-[10px] text-[var(--brand-grey-mute)]">{url}</span>
        </div>
      </div>
      <div className="relative flex-1">{children}</div>
    </div>
  );
}

// Beat-specific backdrop (the element BEHIND the front phone). Crossfades on
// step change. Always shows a contextually-matching, visually-distinct asset.
function FilmBackdrop({ beat }) {
  const { t } = useTranslation("landing");
  const tag = t(`film.beats.${beat.key}.tag`);
  const b = beat.back;
  return (
    <AnimatePresence initial={false}>
      <motion.div
        key={beat.num + b.type}
        className="absolute inset-0 z-10"
        initial={{ opacity: 0, scale: 0.985 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.985 }}
        transition={{ duration: 0.55, ease: EASE }}
      >
        {b.type === "browser" && (
          <div className="absolute left-0 top-1/2 w-[84%] -translate-y-1/2 [transform:perspective(1600px)_rotateY(2deg)]">
            <BrowserFrame url={b.url}>
              <img src={b.src} alt={`${tag} Desktop`} className={screenImg} />
            </BrowserFrame>
          </div>
        )}
        {b.type === "phone" && (
          <div className="absolute left-[4%] top-1/2 aspect-[300/620] h-[62%] -translate-y-1/2 -rotate-[6deg]">
            <PhoneFrame>
              <img src={b.src} alt={`${tag} Detail`} className={screenImg} />
            </PhoneFrame>
          </div>
        )}
        {b.type === "panel" && (
          <div className="absolute left-[6%] top-1/2 aspect-[1119/1744] h-[80%] -translate-y-1/2 [transform:perspective(1600px)_rotateY(3deg)]">
            <PortraitPanel url={b.url}>
              <img src={b.src} alt={`${tag} Desktop`} className={screenImg} />
            </PortraitPanel>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function PhoneScreen({ beat }) {
  const { t } = useTranslation("landing");
  const tag = t(`film.beats.${beat.key}.tag`);
  return (
    <AnimatePresence initial={false}>
      {beat.video ? (
        <motion.div
          key="beat-video"
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.04 }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          <LazyVideo
            sources={{ mp4: lpVideo.mobile_dealflow.mp4 }}
            poster={lpVideo.mobile_dealflow.poster}
            className={screenImg}
            ariaLabel="DealFlow Swipe"
          />
        </motion.div>
      ) : (
        <motion.img
          key={beat.phone}
          src={beat.phone}
          alt={tag}
          className={screenImg}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.04 }}
          transition={{ duration: 0.5, ease: EASE }}
        />
      )}
    </AnimatePresence>
  );
}

function ProductFilmSection() {
  const { t } = useTranslation("landing");
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end end"] });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const idx = Math.max(0, Math.min(BEATS.length - 1, Math.floor(p * BEATS.length)));
    setActive((prev) => (prev === idx ? prev : idx));
  });
  const beat = BEATS[active];

  return (
    <>
      {/* DESKTOP: pinned device stack + scroll-spy */}
      <section
        id="film"
        ref={wrapRef}
        data-testid="landing-v2-film"
        className={`relative hidden lg:block ${T.bg}`}
        style={{ height: `${BEATS.length * 78}vh` }}
      >
        <div className="sticky top-0 flex h-screen items-center overflow-hidden">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(45%_50%_at_30%_50%,rgba(34,211,238,0.08),transparent_70%)]" />
          <div className="relative mx-auto grid w-full max-w-7xl grid-cols-[1.05fr_0.95fr] items-center gap-10 px-10">
            {/* device stage */}
            <div className="relative h-[82vh] max-h-[700px] w-full">
              <FilmBackdrop beat={beat} />
              <motion.div
                className="absolute bottom-[2%] right-[1%] z-20 aspect-[300/620] h-[76%]"
                animate={{ scale: beat.emphasizePhone ? 1.06 : 1 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                {/* active-step cyan glow */}
                <div className="pointer-events-none absolute -inset-7 -z-10 rounded-[3rem] bg-[radial-gradient(closest-side,rgba(34,211,238,0.22),transparent)]" />
                <PhoneFrame testId="film-phone">
                  <PhoneScreen beat={beat} />
                </PhoneFrame>
              </motion.div>
            </div>
            {/* narrative */}
            <div>
              <FilmStepper active={active} />
              <AnimatePresence mode="wait">
                <motion.div
                  key={beat.num}
                  initial={{ opacity: 0, y: 18, scale: 0.985 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -14, scale: 0.985 }}
                  transition={{ duration: 0.4, ease: EASE }}
                >
                  <span className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--brand-cyan)]">
                    {t(`film.beats.${beat.key}.tag`)}
                  </span>
                  <h2 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-white xl:text-[2.7rem]">
                    {t(`film.beats.${beat.key}.h`)}
                  </h2>
                  <p className="mt-4 max-w-md text-lg leading-relaxed text-[var(--brand-white-soft)]">{t(`film.beats.${beat.key}.t`)}</p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* MOBILE: stacked beats */}
      <section data-testid="landing-v2-film-mobile" className={`${T.bg} lg:hidden`}>
        <div className="px-5 pt-12 pb-2 text-center">
          <Eyebrow>{t("film.mobile_eyebrow")}</Eyebrow>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-white">{t("film.mobile_title")}</h2>
        </div>
        {BEATS.map((b) => (
          <Reveal key={b.num} className="border-t border-white/[0.06] px-5 py-10">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--brand-cyan)]">
              {b.num} &middot; {t(`film.beats.${b.key}.tag`)}
            </span>
            <h3 className="mt-2 text-2xl font-bold tracking-tight text-white">{t(`film.beats.${b.key}.h`)}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[var(--brand-white-soft)]">{t(`film.beats.${b.key}.t`)}</p>
            <div className="mx-auto mt-7 aspect-[300/620] w-[220px]">
              <PhoneFrame>
                {b.video ? (
                  <LazyVideo
                    sources={{ mp4: lpVideo.mobile_dealflow.mp4 }}
                    poster={lpVideo.mobile_dealflow.poster}
                    className={screenImg}
                    ariaLabel="DealFlow Swipe"
                  />
                ) : (
                  <img src={b.phone} alt={t(`film.beats.${b.key}.tag`)} className={screenImg} loading="lazy" />
                )}
              </PhoneFrame>
            </div>
          </Reveal>
        ))}
      </section>
    </>
  );
}

// --- Top Deals -------------------------------------------------------- //
function TopDealsSection() {
  const { t } = useTranslation("landing");
  const features = t("topdeals.features", { returnObjects: true }) || [];
  return (
    <section data-testid="landing-v2-topdeals" className={`${T.bg} scroll-mt-20 border-t border-white/[0.06] py-16 lg:py-20`}>
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[0.85fr_1.15fr] lg:px-10">
        <Reveal>
          <Eyebrow>{t("topdeals.eyebrow")}</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold leading-tight tracking-tight text-white">
            {t("topdeals.title")}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-[var(--brand-white-soft)]">
            {t("topdeals.body")}
          </p>
          <ul className="mt-6 space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-[15px] text-[var(--brand-white-soft)]">
                <Check className="h-4 w-4 text-[var(--brand-cyan)]" strokeWidth={2.5} />
                {f}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1} className="relative">
          <div className="pointer-events-none absolute -inset-8 -z-10 bg-[radial-gradient(50%_50%_at_50%_50%,rgba(34,211,238,0.12),transparent_70%)]" />
          <BrowserFrame url="dealalert.ai/top-deals" testId="topdeals-browser">
            <img src={lpImg.topDeals.desktop} alt="Top Deals Desktop" className={screenImg} loading="lazy" />
          </BrowserFrame>
          <div className="absolute -bottom-8 -left-2 aspect-[300/620] h-[78%] sm:h-[86%]">
            <PhoneFrame testId="topdeals-phone">
              <img src={lpImg.topDeals.mobile} alt="Top Deals Mobile" className={screenImg} loading="lazy" />
            </PhoneFrame>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// --- AI Advisor deep dive --------------------------------------------- //
function AiAdvisorSection() {
  const { t } = useTranslation("landing");
  const pills = t("advisor.pills", { returnObjects: true }) || [];
  return (
    <section
      id="ai-advisor"
      data-testid="landing-v2-ai"
      className="relative scroll-mt-20 overflow-hidden border-t border-white/[0.06] bg-[var(--brand-navy-deep)] py-16 lg:py-20"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(55%_50%_at_70%_30%,rgba(34,211,238,0.12),transparent_70%)]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:px-10">
        <Reveal>
          <Eyebrow>{t("advisor.eyebrow")}</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold leading-tight tracking-tight text-white">
            {t("advisor.title")}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-[var(--brand-white-soft)]">
            {t("advisor.body")}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {pills.map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(34,211,238,0.25)] bg-[rgba(34,211,238,0.06)] px-3.5 py-2 text-[13px] text-[var(--brand-white-soft)]"
              >
                <Sparkles className="h-3.5 w-3.5 text-[var(--brand-cyan)]" />
                {p}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="relative flex items-end justify-center gap-4 lg:justify-end">
          {/* desktop AI chat (portrait window) */}
          <div className="relative w-[58%] max-w-[360px]">
            <div className="relative w-full overflow-hidden rounded-xl bg-[var(--brand-navy-card)] ring-1 ring-white/[0.12] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.82)]">
              <div className="flex h-9 items-center gap-2 border-b border-white/[0.07] bg-white/[0.02] px-3.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/80" />
                <div className="ml-3 flex h-5 max-w-[200px] flex-1 items-center rounded-md border border-white/[0.06] bg-white/[0.05] px-2.5">
                  <span className="truncate text-[10px] text-[var(--brand-grey-mute)]">dealalert.ai/advisor</span>
                </div>
              </div>
              <div className="relative aspect-[1119/1744] w-full">
                <img src={lpImg.aiAdvisor.desktop} alt="AI Advisor Desktop" className={screenImg} loading="lazy" />
              </div>
            </div>
          </div>
          {/* mobile AI chat */}
          <div className="aspect-[300/620] w-[40%] max-w-[210px]">
            <PhoneFrame testId="ai-phone">
              <img src={lpImg.aiAdvisor.mobile} alt="AI Advisor Mobile" className={screenImg} loading="lazy" />
            </PhoneFrame>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// --- Favorites + Preferences (double) --------------------------------- //
function MiniShowcase({ title, body, desktop, mobile, icon: Icon, urlPath, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 lg:p-7">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[rgba(34,211,238,0.1)] text-[var(--brand-cyan)]">
          <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
        </span>
        <h3 className="text-xl font-semibold text-white">{title}</h3>
      </div>
      <p className="mt-3 text-[15px] leading-relaxed text-[var(--brand-white-soft)]">{body}</p>
      <div className="relative mt-7 h-[300px]">
        <div className="absolute left-0 top-2 w-[78%]">
          <BrowserFrame url={`dealalert.ai/${urlPath}`}>
            <img src={desktop} alt={`${title} Desktop`} className={screenImg} loading="lazy" />
          </BrowserFrame>
        </div>
        <div className="absolute bottom-0 right-0 aspect-[300/620] h-[92%]">
          <PhoneFrame>
            <img src={mobile} alt={`${title} Mobile`} className={screenImg} loading="lazy" />
          </PhoneFrame>
        </div>
      </div>
    </div>
  );
}

function FavoritesPreferencesSection() {
  const { t } = useTranslation("landing");
  return (
    <section data-testid="landing-v2-fav-pref" className={`${T.bg} scroll-mt-20 border-t border-white/[0.06] py-16 lg:py-20`}>
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>{t("favorites.eyebrow")}</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold leading-tight tracking-tight text-white">
            {t("favorites.title")}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-[var(--brand-white-soft)]">
            {t("favorites.body")}
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <MiniShowcase
              testId="showcase-preferences"
              title={t("favorites.preferences.title")}
              body={t("favorites.preferences.body")}
              desktop={lpImg.preferences.desktop}
              mobile={lpImg.preferences.mobile}
              icon={SlidersHorizontal}
              urlPath="preferences"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <MiniShowcase
              testId="showcase-favorites"
              title={t("favorites.favorites.title")}
              body={t("favorites.favorites.body")}
              desktop={lpImg.favorites.desktop}
              mobile={lpImg.favorites.mobile}
              icon={Heart}
              urlPath="favorites"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// --- Share & Earn ----------------------------------------------------- //
const SHARE_ICONS = [Share2, ShieldCheck, TrendingUp];

function ShareAndEarnSection() {
  const { t } = useTranslation("landing");
  const features = t("share.features", { returnObjects: true }) || [];
  return (
    <section
      data-testid="landing-v2-share"
      className="relative scroll-mt-20 overflow-hidden border-t border-white/[0.06] bg-[var(--brand-navy-deep)] py-16 lg:py-20"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_50%_at_30%_40%,rgba(34,211,238,0.1),transparent_70%)]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:px-10">
        <Reveal>
          <Eyebrow>{t("share.eyebrow")}</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold leading-tight tracking-tight text-white">{t("share.title")}</h2>
          <p className="mt-4 text-lg leading-relaxed text-[var(--brand-white-soft)]">
            {t("share.body")}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {features.map((label, i) => {
              const Icon = SHARE_ICONS[i] || Share2;
              return (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-2 text-[13px] text-[var(--brand-white-soft)]"
                >
                  <Icon className="h-3.5 w-3.5 text-[var(--brand-cyan)]" strokeWidth={2} />
                  {label}
                </span>
              );
            })}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="relative">
          <BrowserFrame url="dealalert.ai/share-and-earn" testId="share-browser">
            <img src={lpImg.shareAndEarn.desktop} alt="Share & Earn Desktop" className={screenImg} loading="lazy" />
          </BrowserFrame>
          <div className="absolute -bottom-8 -right-2 aspect-[300/620] h-[80%] sm:h-[88%]">
            <PhoneFrame testId="share-phone">
              <img src={lpImg.shareAndEarn.mobile_conditions} alt="Share & Earn Conditions" className={screenImg} loading="lazy" />
            </PhoneFrame>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// --- Pricing ---------------------------------------------------------- //
function PricingSection() {
  const { t } = useTranslation("landing");
  const { display: priceDisplay } = usePricing();
  const bullets = t("pricing.bullets", { returnObjects: true }) || [];
  return (
    <section id="pricing" data-testid="landing-v2-pricing" className={`${T.bg} scroll-mt-20 border-t border-white/[0.06] py-16 lg:py-20`}>
      <div className="mx-auto max-w-3xl px-5 text-center lg:px-10">
        <Reveal>
          <Eyebrow>{t("pricing.eyebrow")}</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-white">{t("pricing.title")}</h2>
        </Reveal>
        <Reveal delay={0.1} className="mx-auto mt-10 max-w-md">
          <div className="relative overflow-hidden rounded-2xl border border-[rgba(34,211,238,0.3)] bg-white/[0.03] p-8 text-left shadow-[0_30px_80px_-30px_rgba(34,211,238,0.4)]">
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.25),transparent_70%)]" />
            <div className="flex items-baseline gap-1.5">
              <span className="text-5xl font-bold text-white">{priceDisplay || t("pricing.price")}</span>
              <span className="text-base text-[var(--brand-white-soft)]">{t("pricing.per")}</span>
            </div>
            <ul className="mt-7 space-y-3.5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-[var(--brand-white-soft)]">
                  <Check className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[var(--brand-cyan)]" strokeWidth={2.5} />
                  {b}
                </li>
              ))}
            </ul>
            <Link
              to={REGISTER}
              data-testid="pricing-cta"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--brand-cyan)] px-6 py-3 text-base font-semibold text-[var(--brand-navy-card)] transition-all hover:brightness-110"
            >
              {t("pricing.cta")}
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
            <p className="mt-4 text-center text-xs text-[var(--brand-grey-mute)]">
              {t("pricing.note")}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// --- Final CTA -------------------------------------------------------- //
function FinalCTASection() {
  const { t } = useTranslation("landing");
  return (
    <section data-testid="landing-v2-final-cta" className="relative overflow-hidden border-t border-white/[0.06] bg-[var(--brand-navy-deep)] py-16 lg:py-20">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_60%_at_50%_50%,rgba(34,211,238,0.14),transparent_70%)]" />
      <Reveal className="relative mx-auto max-w-2xl px-5 text-center">
        <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">{t("final.title")}</h2>
        <p className="mx-auto mt-5 max-w-lg text-lg text-[var(--brand-white-soft)]">
          {t("final.body")}
        </p>
        <div className="mt-8 flex flex-col items-center gap-5">
          <Link
            to={REGISTER}
            data-testid="final-cta-primary"
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-cyan)] px-7 py-3.5 text-base font-semibold text-[var(--brand-navy-card)] shadow-[0_10px_40px_-12px_rgba(34,211,238,0.7)] transition-all hover:brightness-110"
          >
            {t("final.cta")}
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
          </Link>
          <StoreBadges className="justify-center" />
        </div>
      </Reveal>
    </section>
  );
}

// --- Footer ----------------------------------------------------------- //
function FooterChrome() {
  const { t } = useTranslation("landing");
  return (
    <footer data-testid="landing-v2-footer" className="border-t border-white/[0.06] bg-[var(--brand-navy-900)] py-12">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex flex-col justify-between gap-8 md:flex-row">
          <div className="max-w-sm">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[var(--brand-cyan)]" />
              <span className="text-[15px] font-semibold text-white">DealAlert</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[var(--brand-grey-mute)]">
              {t("footer.tagline")}
            </p>
          </div>
          <div className="flex gap-14">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-grey-mute)]">{t("footer.product_heading")}</p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li><a href="#film" className="text-[var(--brand-white-soft)] hover:text-white">{t("footer.links.product")}</a></li>
                <li><a href="#ai-advisor" className="text-[var(--brand-white-soft)] hover:text-white">{t("footer.links.ai_advisor")}</a></li>
                <li><a href="#pricing" className="text-[var(--brand-white-soft)] hover:text-white">{t("footer.links.pricing")}</a></li>
                <li><Link to={REGISTER} className="text-[var(--brand-white-soft)] hover:text-white">{t("footer.links.create_account")}</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-grey-mute)]">{t("footer.legal_heading")}</p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li><Link to="/impressum" className="text-[var(--brand-white-soft)] hover:text-white">{t("footer.links.impressum")}</Link></li>
                <li><Link to="/datenschutz" className="text-[var(--brand-white-soft)] hover:text-white">{t("footer.links.datenschutz")}</Link></li>
                <li><Link to="/agb" className="text-[var(--brand-white-soft)] hover:text-white">{t("footer.links.agb")}</Link></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-10 border-t border-white/[0.06] pt-6">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-[var(--brand-grey-mute)]">
            <span>{t("footer.credit")}</span>
            <a
              href="https://cloudservice.hr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cloudservice-link"
              className="font-medium text-[var(--brand-white-soft)] transition-colors hover:text-[var(--brand-cyan)]"
            >
              cloudservice.hr
            </a>
            <a
              href="https://www.instagram.com/cloudservice.hr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="cloudservice.hr auf Instagram"
              className="inline-flex text-[var(--brand-grey-mute)] transition-colors hover:text-[var(--brand-cyan)]"
            >
              <Instagram className="h-4 w-4" strokeWidth={2} />
            </a>
          </div>
          <div className="mt-3 flex flex-col gap-2 text-xs text-[var(--brand-grey-mute)] sm:flex-row sm:items-center sm:justify-between">
            <span>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
            <span>{t("footer.tags")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// --- Page ------------------------------------------------------------- //
export default function LandingV2() {
  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "var(--brand-navy-900)";
    // The global stylesheet sets `#root { overflow-x: hidden }`, which turns
    // #root into a non-scrolling scroll container and BREAKS position:sticky
    // for descendants (the Product Film would scroll away instead of pinning).
    // While the landing is mounted we switch #root to overflow-x:clip via a
    // body class (see src/styles/brand-v2.css). Scoped: only affects "/".
    document.body.classList.add("dealalert-landing");
    // bfcache-safe cleanup: iOS Safari can restore the page via back-forward
    // cache without triggering React unmount/cleanup. When that happens on a
    // non-`/` route, the `dealalert-landing` class would leak onto Dashboard
    // /DealFlow and apply `overflow-x: clip` to #root globally. The pageshow
    // listener strips the class on restored pages that are NOT the landing.
    const onPageShow = (e) => {
      if (e.persisted && window.location.pathname !== "/") {
        document.body.classList.remove("dealalert-landing");
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.body.style.backgroundColor = prevBg;
      document.body.classList.remove("dealalert-landing");
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  return (
    <div
      data-testid="landing-v2-root"
      className={`${T.bg} min-h-screen text-white antialiased selection:bg-[var(--brand-cyan)]/35 selection:text-white`}
    >
      <TopNav />
      <main>
        <HeroSection />
        <ProofStrip />
        <ProductFilmSection />
        <TopDealsSection />
        <AiAdvisorSection />
        <FavoritesPreferencesSection />
        <ShareAndEarnSection />
        <PricingSection />
        <FinalCTASection />
      </main>
      <FooterChrome />
    </div>
  );
}
